/**
 * Blocks 카탈로그 페이지 — `/blocks` 라우트.
 *
 * 4층 구조(Block → Pack → Preset → Slice) 의 최하단 "블럭" 을 서비스와 무관하게 브라우즈한다.
 * 팩 내부에 숨어있던 블럭 단위를 전면에 노출해, 운영자가 "어떤 팩이 어떤 블럭을 조합하는가"
 * 를 역방향으로도 탐색할 수 있게 한다.
 *
 * 구조:
 * - 상단 헤더: 제목 + 총 개수 stats
 * - **뷰 모드 탭** (2가지): Catalog (카드 그리드) · Master Graph (Cosmograph)
 *   - Catalog 탭: 카테고리 탭 + 검색 + 풍부한 블럭 카드 (relations/shapes 상세)
 *   - Master Graph 탭: 마스터 블럭(현재 111, BLOCKS_TUPLE.length SSOT) × master shape 전체 그래프 (노드 클릭 → 모달 열기)
 * - 카드/노드 클릭 시 `BlockPreviewModal` 로 상세 프리뷰 오픈
 *
 * 데이터 소스:
 * - `client.getOntologyBlocks()` + `client.getOntologyPacks()` + `client.getMasterShapes()` 를 병렬 로드
 * - 팩 역참조는 `packsUsingBlock(packs, block.id)` 로 계산
 * - Master Graph 는 `buildBlocksGraphData({ blocks, masterShapes })` 로 변환
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Term } from "@/shared/components/tooltip/Term.tsx"
import {
	AdminClient,
	type BlockCategory,
	type OntologyBlock,
	type OntologyPack,
	type ShapeDef
} from "../api/admin-client.ts"
import type { AuthContext } from "../components/AdminAuthGate.tsx"
import type { GraphNode } from "../components/graph/types.ts"
import { BlockPreviewModal } from "../components/ontology/BlockPreviewModal.tsx"
import { PackPreviewModal } from "../components/ontology/PackPreviewModal.tsx"
import { blocksByCategory, packsUsingBlock } from "../components/ontology/pack-block-utils.ts"
import { Skeleton, SkeletonText } from "../components/Skeleton.tsx"
import { useAsyncData } from "../hooks/useAsyncData.ts"
import { useInfiniteScroll } from "../hooks/useInfiniteScroll.ts"
import { BlockPicker } from "./blocks-graph/BlockPicker.tsx"
import { type MasterGraphHandle, MasterGraphView } from "./blocks-graph/MasterGraphView.tsx"
import { WorkbenchChat } from "./blocks-graph/WorkbenchChat.tsx"
import { type SlicePreview, WorkbenchSlicePreview } from "./blocks-graph/WorkbenchSlicePreview.tsx"
import { buildBlocksGraphData, buildKindToBlockMap } from "./blocks-graph-data.ts"

/** 카테고리 탭 — "all" 은 전체 필터 해제를 의미. 나머지는 `BlockCategory` 와 1:1. */
type CategoryFilter = "all" | BlockCategory

/** 뷰 모드 — Catalog (카드 그리드) · Master Graph (force-graph). 이제 라우트에서 결정된다. */
export type ViewMode = "catalog" | "graph"

/**
 * 카테고리 탭 정의 — 순서는 블럭 카탈로그의 카테고리 순서와 일치
 * (actor → content → event → product → place → structure → decision).
 */
const CATEGORY_TABS: ReadonlyArray<{ readonly key: CategoryFilter; readonly label: string }> = [
	{ key: "all", label: "전체" },
	{ key: "actor", label: "행위자" },
	{ key: "content", label: "콘텐츠" },
	{ key: "event", label: "이벤트" },
	{ key: "product", label: "제품" },
	{ key: "place", label: "장소" },
	{ key: "structure", label: "구조" },
	{ key: "decision", label: "결정" }
]

// 카탈로그 미로드 시 fallback 빈 배열 — 모듈 상수로 고정해 매 렌더 새 배열 생성을 막아
// 하위 useMemo 의존성(blocks/packs/masterShapes)이 불필요하게 무효화되지 않도록 한다.
const EMPTY_BLOCKS: ReadonlyArray<OntologyBlock> = []
const EMPTY_PACKS: ReadonlyArray<OntologyPack> = []
const EMPTY_SHAPES: ReadonlyArray<ShapeDef> = []

export function BlocksPage(props: { readonly auth: AuthContext; readonly mode?: ViewMode }) {
	const viewMode: ViewMode = props.mode ?? "catalog"
	const [category, setCategory] = useState<CategoryFilter>("all")
	const [query, setQuery] = useState("")
	const [previewBlock, setPreviewBlock] = useState<OntologyBlock | undefined>()
	const [previewPack, setPreviewPack] = useState<OntologyPack | undefined>()

	const client = useMemo(
		() => new AdminClient(props.auth.baseUrl, props.auth.invalidateSession),
		[props.auth.baseUrl, props.auth.invalidateSession]
	)
	const graphRef = useRef<MasterGraphHandle>(null)
	// `?block=<id>` search param 과 previewBlock 을 동기화 — 카드 클릭 시 URL 반영 / 링크 공유
	// 시 자동 모달 오픈. /blocks?block=persona 처럼 붙여서 공유하면 받은 쪽도 해당 블럭 상세
	// 를 바로 본다.
	const [searchParams, setSearchParams] = useSearchParams()

	// 블럭 + 팩 + 마스터 셰이프 카탈로그를 한 번에 로드.
	const { data: catalog, error } = useAsyncData<{
		readonly blocks: ReadonlyArray<OntologyBlock>
		readonly packs: ReadonlyArray<OntologyPack>
		readonly masterShapes: ReadonlyArray<ShapeDef>
	}>(async () => {
		const [blocks, packs, masterShapes] = await Promise.all([
			client.getOntologyBlocks(),
			client.getOntologyPacks(),
			client.getMasterShapes()
		])
		return { blocks, packs, masterShapes }
	}, [client])
	const blocks = catalog?.blocks ?? EMPTY_BLOCKS
	const packs = catalog?.packs ?? EMPTY_PACKS
	const masterShapes = catalog?.masterShapes ?? EMPTY_SHAPES
	// 스켈레톤은 최초 로드(아직 데이터 없음)에만 — 기존 loading=true 초기값 동등성.
	const loading = !error && catalog === undefined

	// 카테고리별 그룹핑 — 탭 카운트 배지에 사용
	const byCategory = useMemo(() => blocksByCategory(blocks), [blocks])

	// 검색어 + 카테고리 적용된 표시 대상 (Catalog 탭 전용).
	// 매칭 필드: 블럭 displayName / description / kinds[].name + 이 블럭을 쓰는 pack displayName
	// → pack chip 클릭으로 pack.displayName 을 query 에 넣으면 그 pack 이 쓰는 블럭만 남는다.
	const filtered = useMemo((): ReadonlyArray<OntologyBlock> => {
		const q = query.trim().toLowerCase()
		const pool = category === "all" ? blocks : byCategory[category]
		if (!q) return pool
		return pool.filter((b) => {
			if (b.displayName.toLowerCase().includes(q)) return true
			if (b.description.toLowerCase().includes(q)) return true
			for (const kind of b.slice.kinds) {
				if (kind.name.toLowerCase().includes(q)) return true
			}
			for (const pack of packsUsingBlock(packs, b.id)) {
				if (pack.displayName.toLowerCase().includes(q)) return true
			}
			return false
		})
	}, [blocks, byCategory, category, query, packs])

	// kind 이름 → 블럭 매핑 — ShapeFlow / BlockCard 에서 재사용. 매 렌더마다 재계산 방지.
	const kindToBlock = useMemo(() => buildKindToBlockMap(blocks), [blocks])

	// 마스터 그래프 데이터 — Catalog 탭에서도 preview 모달에 masterShapes 가 필요하므로 미리 준비
	const graphData = useMemo(() => buildBlocksGraphData({ blocks, masterShapes }), [blocks, masterShapes])

	// blockId → 블럭 역참조 — 그래프 노드 클릭 시 블럭 객체를 찾는 용도
	const blockById = useMemo(() => {
		const map = new Map<string, OntologyBlock>()
		for (const b of blocks) map.set(b.id, b)
		return map
	}, [blocks])

	// URL `?block=<id>` → previewBlock 동기화 (최초 진입/뒤로가기 대응).
	useEffect(() => {
		const id = searchParams.get("block")
		if (id) {
			const b = blockById.get(id)
			if (b && b.id !== previewBlock?.id) setPreviewBlock(b)
		} else if (previewBlock) {
			setPreviewBlock(undefined)
		}
	}, [searchParams, blockById, previewBlock])

	/** 카드/그래프 노드 클릭 → 모달 열기 + `?block=` search param 추가. */
	const openPreview = (b: OntologyBlock) => {
		setPreviewBlock(b)
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev)
				next.set("block", b.id)
				return next
			},
			{ replace: false }
		)
	}

	/** 모달 닫기 → `?block=` 제거 (공유 URL 시 이전 상태 복원). */
	const closePreview = () => {
		setPreviewBlock(undefined)
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev)
				next.delete("block")
				return next
			},
			{ replace: true }
		)
	}

	const filterActive = query.trim().length > 0 || category !== "all"

	function handleGraphNodeSelect(node: GraphNode | null) {
		if (!node) return
		const block = blockById.get(node.id)
		if (block) openPreview(block)
	}

	return (
		<div className="content is-wide">
			<div className="page-head">
				<div>
					<h2>
						{viewMode === "graph" ? (
							<>온톨로지 워크벤치</>
						) : (
							<>
								온톨로지 <Term name="block">블럭</Term>
							</>
						)}
					</h2>
					<div className="sub">
						{viewMode === "graph" ? (
							<>에이전트와 의논하며 블럭/팩 카탈로그를 탐색하고 슬라이스를 큐레이션합니다</>
						) : (
							<>
								`1 <Term name="block">블럭</Term> = 1 <Term name="kind">kind</Term>` 엄격 1:1 의 최소 재사용
								조각. 여러 <Term name="pack">팩</Term> 이 같은 블럭을 공유합니다.
							</>
						)}
					</div>
				</div>
			</div>

			{error && (
				<div className="alert alert-danger" style={{ marginBottom: 18 }}>
					{error}
				</div>
			)}

			<div className="stats">
				<div className="stat">
					<div className="stat-v">
						{loading ? <Skeleton width={48} height={22} radius={6} inline /> : blocks.length}
					</div>
					<div className="stat-k">총 블럭 수</div>
				</div>
				<div className="stat">
					<div className="stat-v">
						{loading ? <Skeleton width={48} height={22} radius={6} inline /> : packs.length}
					</div>
					<div className="stat-k">블럭 사용 팩 수</div>
				</div>
				<div className="stat">
					<div className="stat-v">
						{loading ? <Skeleton width={48} height={22} radius={6} inline /> : masterShapes.length}
					</div>
					<div className="stat-k">마스터 shape 수</div>
				</div>
				<div className="stat">
					<div className="stat-v">
						{loading ? (
							<Skeleton width={48} height={22} radius={6} inline />
						) : (
							new Set(blocks.flatMap((b) => b.slice.kinds.map((k) => k.name))).size
						)}
					</div>
					<div className="stat-k">고유 kind 수</div>
				</div>
			</div>

			{viewMode === "catalog" ? (
				<CatalogView
					loading={loading}
					blocks={blocks}
					filtered={filtered}
					packs={packs}
					masterShapes={masterShapes}
					byCategory={byCategory}
					query={query}
					setQuery={setQuery}
					category={category}
					setCategory={setCategory}
					filterActive={filterActive}
					onOpenPreview={openPreview}
					onFilterByPack={(pack) => setPreviewPack(pack)}
					onClearFilters={() => {
						setQuery("")
						setCategory("all")
					}}
					kindToBlock={kindToBlock}
				/>
			) : (
				<GraphView
					loading={loading}
					blocks={blocks}
					client={client}
					graphData={graphData}
					onNodeSelect={handleGraphNodeSelect}
					graphRef={graphRef}
				/>
			)}

			<p className="muted blocks-page-foot">
				블럭 카탈로그는 `/admin/ontology/blocks` API · 마스터 shape 는 `/admin/ontology/shapes` API 에서
				제공됩니다. 총 {blocks.length}개 블럭 · {masterShapes.length}개 master shape · {packs.length}개 팩이
				조합에 사용 중.
			</p>

			{previewBlock && (
				<BlockPreviewModal
					block={previewBlock}
					allBlocks={blocks}
					allShapes={masterShapes}
					allPacks={packs}
					onClose={closePreview}
				/>
			)}
			{previewPack && (
				<PackPreviewModal pack={previewPack} allBlocks={blocks} onClose={() => setPreviewPack(undefined)} />
			)}
		</div>
	)
}

/** Catalog 뷰 — 카테고리 탭 + 검색 + 풍부한 카드 그리드. */
function CatalogView(props: {
	readonly loading: boolean
	readonly blocks: ReadonlyArray<OntologyBlock>
	readonly filtered: ReadonlyArray<OntologyBlock>
	readonly packs: ReadonlyArray<OntologyPack>
	readonly masterShapes: ReadonlyArray<ShapeDef>
	readonly byCategory: Record<BlockCategory, ReadonlyArray<OntologyBlock>>
	readonly query: string
	readonly setQuery: (q: string) => void
	readonly category: CategoryFilter
	readonly setCategory: (c: CategoryFilter) => void
	readonly filterActive: boolean
	readonly onOpenPreview: (b: OntologyBlock) => void
	readonly onFilterByPack: (pack: OntologyPack) => void
	readonly onClearFilters: () => void
	readonly kindToBlock: ReadonlyMap<string, OntologyBlock>
}) {
	const {
		loading,
		blocks,
		filtered,
		packs,
		masterShapes,
		byCategory,
		query,
		setQuery,
		category,
		setCategory,
		filterActive,
		onOpenPreview,
		onFilterByPack,
		onClearFilters,
		kindToBlock
	} = props

	/** 카드 그리드가 2 열로 넓어지며 세로 길이가 큼 → 무한스크롤로 점진 로드. */
	const {
		visible: paged,
		sentinelRef,
		hasMore,
		loadedCount,
		totalCount
	} = useInfiniteScroll(filtered, {
		pageSize: 20
	})

	return (
		<>
			<div className="blocks-page-toolbar">
				<label className="table-search">
					<svg
						className="icon"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<title>검색</title>
						<circle cx="11" cy="11" r="7" />
						<path d="m21 21-4.3-4.3" />
					</svg>
					<input
						type="search"
						placeholder="이름 · 설명 · kind 이름 검색"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						spellCheck={false}
					/>
				</label>
				<div className="blocks-page-tabs" role="tablist" aria-label="카테고리 필터">
					{CATEGORY_TABS.map((tab) => {
						const isActive = category === tab.key
						const count = tab.key === "all" ? blocks.length : byCategory[tab.key].length
						return (
							<button
								key={tab.key}
								type="button"
								role="tab"
								aria-selected={isActive}
								className={`blocks-page-tab${isActive ? " is-active" : ""}`}
								onClick={() => setCategory(tab.key)}
							>
								{tab.label}
								<span className="blocks-page-tab-count">{count}</span>
							</button>
						)
					})}
				</div>
			</div>

			{loading ? (
				<div className="blocks-page-grid">
					{Array.from({ length: 9 }, (_, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: deterministic skeleton
						<BlockCardSkeleton key={i} />
					))}
				</div>
			) : filtered.length === 0 ? (
				<div className="table-empty">
					<p>조건에 맞는 블럭이 없습니다.</p>
					{filterActive && (
						<button type="button" className="btn btn-sm btn-ghost" onClick={onClearFilters}>
							필터 초기화
						</button>
					)}
				</div>
			) : (
				<>
					<div className="blocks-page-grid">
						{paged.map((block) => (
							<BlockCard
								key={block.id}
								block={block}
								packs={packsUsingBlock(packs, block.id)}
								masterShapes={masterShapes}
								onOpenPreview={() => onOpenPreview(block)}
								onFilterByPack={onFilterByPack}
								kindToBlock={kindToBlock}
							/>
						))}
					</div>
					{totalCount > 0 && (
						<div ref={sentinelRef} className="infinite-scroll-sentinel" role="status" aria-live="polite">
							{hasMore ? `불러오는 중… (${loadedCount} / ${totalCount})` : `전체 ${totalCount}개 표시됨`}
						</div>
					)}
				</>
			)}
		</>
	)
}

/**
 * Master Graph 뷰 — **블럭 조합 플레이그라운드**.
 *
 * 플레이그라운드 패턴으로 통합된 3-column 인라인 레이아웃:
 * - 좌측 (50%): MasterGraphView (캔버스)
 * - 중앙 (28%): WorkbenchChat (에이전트 채팅 + suggest_slice 결과 프리뷰)
 * - 우측 (22%): BlockPicker (마스터 블럭 토글 패널, 작은 화면에서 collapse)
 *
 * 드로어 없이 항상 인라인 배치 — 헤더에 "에이전트 호출" 버튼 제거.
 * URL `?blocks=id1,id2,...` 로 조합 공유 가능. 기존 `?block=<id>` preview param 과 공존.
 */
function GraphView(props: {
	readonly loading: boolean
	readonly blocks: ReadonlyArray<OntologyBlock>
	readonly client: AdminClient
	readonly graphData: {
		readonly nodes: ReadonlyArray<GraphNode>
		readonly edges: ReadonlyArray<import("../components/graph/types.ts").GraphEdge>
	}
	readonly onNodeSelect: (node: GraphNode | null) => void
	readonly graphRef: React.RefObject<MasterGraphHandle | null>
}) {
	const { loading, blocks, client, graphData, onNodeSelect, graphRef } = props
	const [searchParams, setSearchParams] = useSearchParams()

	/** suggest_slice 결과 미리보기 — 비어 있으면 카드 비표시. */
	const [slicePreview, setSlicePreview] = useState<SlicePreview | undefined>()

	/**
	 * BlockPicker 표시 여부 — 기본은 collapsed (그래프 + 채팅 2-column 으로 채팅 폭 확보).
	 * 헤더의 "블럭 직접 켜기" 토글로 ON/OFF. localStorage 에 세션 간 유지.
	 */
	const PICKER_OPEN_KEY = "pk.blocksPlayground.pickerOpen"
	const [pickerOpen, setPickerOpen] = useState<boolean>(() => {
		if (typeof window === "undefined") return false
		try {
			return window.localStorage.getItem(PICKER_OPEN_KEY) === "1"
		} catch {
			return false
		}
	})
	useEffect(() => {
		if (typeof window === "undefined") return
		try {
			window.localStorage.setItem(PICKER_OPEN_KEY, pickerOpen ? "1" : "0")
		} catch {
			/* ignore */
		}
	}, [pickerOpen])

	// URL `?blocks=` 를 single source of truth — 페이지 새로고침/공유 링크 복구 자동.
	const selectedIds = useMemo<ReadonlySet<string>>(() => {
		const raw = searchParams.get("blocks") ?? ""
		const parts = raw.split(",").filter(Boolean)
		return new Set(parts)
	}, [searchParams])

	const writeSelected = (next: ReadonlySet<string>) => {
		setSearchParams(
			(prev) => {
				const p = new URLSearchParams(prev)
				if (next.size === 0) p.delete("blocks")
				else p.set("blocks", [...next].sort().join(","))
				return p
			},
			{ replace: true }
		)
	}

	const toggleBlock = (id: string) => {
		const next = new Set(selectedIds)
		if (next.has(id)) next.delete(id)
		else next.add(id)
		writeSelected(next)
	}

	const toggleCategory = (cat: BlockCategory, activate: boolean) => {
		const next = new Set(selectedIds)
		for (const b of blocks) {
			if (b.category !== cat) continue
			if (activate) next.add(b.id)
			else next.delete(b.id)
		}
		writeSelected(next)
	}

	const clearAll = () => writeSelected(new Set())

	const applyAdd = (ids: ReadonlyArray<string>) => {
		const next = new Set(selectedIds)
		for (const id of ids) next.add(id)
		writeSelected(next)
	}

	const shareUrl = () => {
		if (typeof window === "undefined") return
		void navigator.clipboard?.writeText(window.location.href).catch(() => undefined)
	}

	/** suggest_slice 결과를 받아 preview 카드 표시 + 그래프 즉시 반영. */
	const handleSuggestSliceResult = (preview: {
		readonly kinds: unknown[]
		readonly relations: unknown[]
		readonly shapes: unknown[]
	}) => {
		// 슬라이스 미리보기 카드 표시
		setSlicePreview(preview as SlicePreview)
		// Optimistic: kinds 에 해당하는 블럭을 즉시 그래프에 추가
		const kindNames = new Set(
			(preview.kinds as Array<{ name?: string }>)
				.map((k) => k?.name)
				.filter((n): n is string => typeof n === "string")
		)
		const matchedBlockIds = blocks
			.filter((b) => b.slice.kinds.some((k) => kindNames.has(k.name)))
			.map((b) => b.id)
		if (matchedBlockIds.length > 0) applyAdd(matchedBlockIds)
	}

	/** "그래프에 적용" — preview 의 kinds 블럭이 이미 추가된 상태이므로 카드만 닫는다. */
	const handleApplySlice = () => {
		setSlicePreview(undefined)
	}

	/** "무시" — preview 카드 닫기. */
	const handleDiscardSlice = () => {
		setSlicePreview(undefined)
	}

	// 선택된 블럭만 필터 — 엣지는 양끝이 모두 선택 안 됐으면 드롭.
	const filteredNodes = useMemo(
		() => graphData.nodes.filter((n) => selectedIds.has(n.id)),
		[graphData.nodes, selectedIds]
	)
	const filteredEdges = useMemo(
		() => graphData.edges.filter((e) => selectedIds.has(e.source) && selectedIds.has(e.target)),
		[graphData.edges, selectedIds]
	)

	return (
		<div className={`blocks-playground${pickerOpen ? " has-picker" : ""}`}>
			{/* 좌측 — 그래프 캔버스 */}
			<div className="blocks-playground__graph">
				<div className="blocks-playground__graph-head">
					<span className="blocks-playground__counts">
						{filteredNodes.length === 0 ? (
							<span className="blocks-playground__counts-empty">선택 없음</span>
						) : (
							<>
								<strong>{filteredNodes.length}</strong>개 블럭 · <strong>{filteredEdges.length}</strong>개
								연결
							</>
						)}
					</span>
					<div className="blocks-playground__graph-actions">
						<button
							type="button"
							className="btn btn-ghost btn-sm"
							onClick={() => setPickerOpen((v) => !v)}
							aria-pressed={pickerOpen}
							title={pickerOpen ? "BlockPicker 패널 닫기" : "BlockPicker 패널 열기 (블럭을 직접 켜기/끄기)"}
						>
							{pickerOpen ? "블럭 패널 닫기" : "블럭 직접 켜기"}
						</button>
						<button
							type="button"
							className="btn btn-ghost btn-sm"
							onClick={() => graphRef.current?.fitView()}
							disabled={filteredNodes.length === 0}
							title="전체 그래프가 화면에 들어오도록 맞춤"
						>
							Fit
						</button>
						<button
							type="button"
							className="btn btn-ghost btn-sm"
							onClick={shareUrl}
							disabled={selectedIds.size === 0}
							title="현재 조합을 포함한 URL 을 클립보드에 복사"
						>
							링크 복사
						</button>
					</div>
				</div>
				<div className="blocks-playground__graph-canvas">
					{loading ? (
						<Skeleton width="100%" height="100%" radius={12} />
					) : (
						<MasterGraphView
							ref={graphRef}
							nodes={filteredNodes}
							edges={filteredEdges}
							onNodeSelect={onNodeSelect}
						/>
					)}
				</div>
			</div>

			{/* 중앙 — 워크벤치 에이전트 채팅 사이드 */}
			<div className="blocks-playground__chat">
				{/* suggest_slice 결과 미리보기 카드 (도구 호출 후 등장) */}
				{slicePreview && (
					<WorkbenchSlicePreview
						preview={slicePreview}
						onApply={handleApplySlice}
						onDiscard={handleDiscardSlice}
					/>
				)}
				<WorkbenchChat
					client={client}
					onSearchBlocksResult={(ids) => {
						// search_blocks 결과 블럭을 선택 목록에 추가 → MasterGraphView 즉시 반영
						applyAdd([...ids])
					}}
					onSuggestSliceResult={handleSuggestSliceResult}
				/>
			</div>

			{/* 우측 — BlockPicker (토글 ON 시에만 마운트, off 면 DOM 자체 미생성) */}
			{pickerOpen && (
				<div className="blocks-playground__picker">
					<BlockPicker
						blocks={blocks}
						selectedIds={selectedIds}
						onToggle={toggleBlock}
						onToggleCategory={toggleCategory}
						onClearAll={clearAll}
					/>
				</div>
			)}
		</div>
	)
}

/** 블럭 카드 스켈레톤 — 카드 내부 라인 맞춤 (icon + title + desc 2줄 + chips 2줄). */
function BlockCardSkeleton() {
	return (
		<div className="skel-card" aria-hidden>
			<div className="skel-card__head">
				<Skeleton width={32} height={32} radius={8} />
				<div className="skel-card__title-group">
					<Skeleton width="60%" height={14} />
					<Skeleton width="40%" height={11} />
				</div>
				<Skeleton width={50} height={18} radius={999} />
			</div>
			<SkeletonText lines={2} lineHeight={11} />
			<Skeleton width="80%" height={11} />
			<div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
				<Skeleton width={70} height={20} radius={999} />
				<Skeleton width={60} height={20} radius={999} />
				<Skeleton width={90} height={20} radius={999} />
			</div>
		</div>
	)
}

/**
 * ShapeFlow — shape(`source → relation → target`) 를 3-파트 뱃지 플로우로 시각화.
 *
 * 이전 text code 표기("org — MEMBER_OF → team") 보다 훨씬 가독성 ↑. 각 side 는 해당 block 카드
 * (`#block.id`) 로 앵커 링크되고, block 의 icon + displayName 을 병기한다. relation 은 중앙
 * pill 로 accent 강조. 매칭되는 블럭이 없으면 kind 를 그대로 fallback 표시.
 */
function ShapeFlow(props: {
	readonly sourceKind: string
	readonly relation: string
	readonly targetKind: string
	readonly kindToBlock: ReadonlyMap<string, OntologyBlock>
	/**
	 * 섹션 방향성을 명시적으로 받는다. self-loop 도 각 섹션 의미에 맞게 한쪽만 노출해야 하기
	 * 때문에 `ownKind` 비교가 아닌 호출 측 의도를 신뢰한다.
	 *   - "outgoing": 본인이 source → 왼쪽(source) 생략, `RELATION → tgt`
	 *   - "incoming": 본인이 target → 오른쪽(target) 생략, `src → RELATION`
	 *   - undefined: 양쪽 노출 (일반 카탈로그 맥락)
	 */
	readonly direction?: "outgoing" | "incoming"
}) {
	const src = props.kindToBlock.get(props.sourceKind)
	const tgt = props.kindToBlock.get(props.targetKind)
	const hideSource = props.direction === "outgoing"
	const hideTarget = props.direction === "incoming"
	return (
		<div className="shape-flow">
			{!hideSource && (
				<>
					<ShapeNode block={src} kind={props.sourceKind} />
					<span className="shape-flow__arrow" aria-hidden>
						→
					</span>
				</>
			)}
			<span className="shape-flow__rel-label">{props.relation}</span>
			{!hideTarget && (
				<>
					<span className="shape-flow__arrow" aria-hidden>
						→
					</span>
					<ShapeNode block={tgt} kind={props.targetKind} />
				</>
			)}
		</div>
	)
}

function ShapeNode({ block, kind }: { readonly block: OntologyBlock | undefined; readonly kind: string }) {
	if (!block) {
		return <span className="shape-flow__node shape-flow__node--orphan">{kind}</span>
	}
	return (
		<a
			href={`#${block.id}`}
			className={`shape-flow__node cat-${block.category}`}
			title={block.description}
			onClick={(e) => e.stopPropagation()}
		>
			<span className="shape-flow__icon" aria-hidden>
				{block.icon}
			</span>
			<span className="shape-flow__label">{block.displayName}</span>
		</a>
	)
}

/**
 * 블럭 1 카드 — 아이콘 + 이름 + 설명 + stats + relations 칩 + shapes 요약 + "Used in packs" 칩.
 *
 * id 를 `id` prop 으로 부여해 `/blocks#{blockId}` 링크가 스크롤하도록 한다
 * (OntologyPacksPanel 의 블럭 카드에서 이 앵커로 이동).
 *
 * 카드 우측 상단에 "상세" 버튼 — 클릭 시 `BlockPreviewModal` 오픈.
 */
function BlockCard(props: {
	readonly block: OntologyBlock
	readonly packs: ReadonlyArray<OntologyPack>
	readonly masterShapes: ReadonlyArray<ShapeDef>
	readonly onOpenPreview: () => void
	readonly onFilterByPack: (pack: OntologyPack) => void
	readonly kindToBlock: ReadonlyMap<string, OntologyBlock>
}) {
	const { block, packs, masterShapes, onOpenPreview, onFilterByPack, kindToBlock } = props

	// 이 블럭이 선언한 shape — 카드 미리보기로 2~3개만.
	const ownShapes = block.slice.shapes
	// 상한 — 카드가 너무 길어지지 않도록 제한. 넘는 개수는 "+N more" 로 표시.
	const RELATION_LIMIT = 6
	const SHAPE_LIMIT = 4

	// 마스터 관점에서 이 블럭으로 들어오는 shape 목록 — Outgoing 과 대칭되는 Incoming Shapes 섹션 렌더용.
	const incomingShapes = useMemo(() => {
		const myKinds = new Set(block.slice.kinds.map((k) => k.name))
		return masterShapes.filter((s) => myKinds.has(s.targetKind))
	}, [block, masterShapes])
	const incomingCount = incomingShapes.length

	return (
		// biome-ignore lint/a11y/useSemanticElements: 카드 레이아웃 유지 위해 article + role="button" 의도
		<article
			id={block.id}
			className="blocks-page-card blocks-page-card--interactive"
			// biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: 상동
			role="button"
			tabIndex={0}
			onClick={onOpenPreview}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault()
					onOpenPreview()
				}
			}}
			aria-label={`${block.displayName} 블럭 상세 보기`}
		>
			<header className="blocks-page-card-head">
				<span className="blocks-page-card-icon" aria-hidden>
					{block.icon}
				</span>
				<div className="blocks-page-card-title">
					<strong>{block.displayName}</strong>
				</div>
				<span className={`badge blocks-page-card-cat cat-${block.category}`}>{block.category}</span>
			</header>

			<p className="blocks-page-card-desc">{block.description}</p>

			<div className="blocks-page-card-stats">
				<span>종류 {block.slice.kinds.length}</span>
				<span>관계 {block.slice.relations.length}</span>
				<span>형태 {ownShapes.length}</span>
				<span title="master shape 풀 기준 incoming 개수">들어오는 {incomingCount}</span>
			</div>

			{block.slice.relations.length > 0 && (
				<div className="blocks-page-card-relations">
					<span className="blocks-page-card-relations-label">릴레이션</span>
					{block.slice.relations.slice(0, RELATION_LIMIT).map((r) => (
						<span
							key={r.name}
							className="chip blocks-page-card-relation-chip"
							title={r.description ?? r.displayName}
						>
							{r.name}
						</span>
					))}
					{block.slice.relations.length > RELATION_LIMIT && (
						<span className="muted blocks-page-card-more">
							+{block.slice.relations.length - RELATION_LIMIT}개 더
						</span>
					)}
				</div>
			)}

			{/* 3-박스 레이아웃 — [Incoming] [This block] [Outgoing]. 중앙 마커로 흐름이 `외부 → 본인 → 외부` 시각화. */}
			{(incomingShapes.length > 0 || ownShapes.length > 0) && (
				<div className="blocks-page-card-shapes-wrapper">
					<div className="blocks-page-card-shapes blocks-page-card-shapes--incoming">
						<span className="blocks-page-card-shapes-label">들어오는 연결</span>
						{incomingShapes.length > 0 ? (
							<ul className="blocks-page-card-shape-list">
								{incomingShapes.slice(0, SHAPE_LIMIT).map((s) => (
									<li
										key={`in:${s.sourceKind}|${s.relation}|${s.targetKind}`}
										className="blocks-page-card-shape"
										title={s.description}
									>
										<ShapeFlow
											sourceKind={s.sourceKind}
											relation={s.relation}
											targetKind={s.targetKind}
											kindToBlock={kindToBlock}
											direction="incoming"
										/>
									</li>
								))}
							</ul>
						) : (
							<span className="muted blocks-page-card-more">없음</span>
						)}
						{incomingShapes.length > SHAPE_LIMIT && (
							<span className="muted blocks-page-card-more">+{incomingShapes.length - SHAPE_LIMIT}개 더</span>
						)}
					</div>

					<div className="blocks-page-card-shapes-arrow" aria-hidden>
						→
					</div>

					<div className="blocks-page-card-shapes-self" aria-hidden>
						<span className="blocks-page-card-shapes-self-icon">{block.icon}</span>
						<span className="blocks-page-card-shapes-self-name">{block.displayName}</span>
					</div>

					<div className="blocks-page-card-shapes-arrow" aria-hidden>
						→
					</div>

					<div className="blocks-page-card-shapes blocks-page-card-shapes--outgoing">
						<span className="blocks-page-card-shapes-label">나가는 연결</span>
						{ownShapes.length > 0 ? (
							<ul className="blocks-page-card-shape-list">
								{ownShapes.slice(0, SHAPE_LIMIT).map((s) => (
									<li
										key={`${s.sourceKind}|${s.relation}|${s.targetKind}`}
										className="blocks-page-card-shape"
										title={s.description}
									>
										<ShapeFlow
											sourceKind={s.sourceKind}
											relation={s.relation}
											targetKind={s.targetKind}
											kindToBlock={kindToBlock}
											direction="outgoing"
										/>
									</li>
								))}
							</ul>
						) : (
							<span className="muted blocks-page-card-more">없음</span>
						)}
						{ownShapes.length > SHAPE_LIMIT && (
							<span className="muted blocks-page-card-more">+{ownShapes.length - SHAPE_LIMIT}개 더</span>
						)}
					</div>
				</div>
			)}

			<div className="blocks-page-card-packs">
				<span className="blocks-page-card-packs-label">사용 팩</span>
				{packs.length === 0 ? (
					<span className="muted" style={{ fontSize: 11 }}>
						아직 사용 팩 없음
					</span>
				) : (
					packs.map((p) => (
						<button
							key={p.id}
							type="button"
							className="chip blocks-page-card-pack"
							title={`${p.description} — 클릭 시 이 팩의 블럭만 필터`}
							onClick={(e) => {
								e.stopPropagation()
								onFilterByPack(p)
							}}
						>
							{p.icon} {p.displayName}
						</button>
					))
				)}
			</div>
		</article>
	)
}
